import React from "react";
import { Col, Container, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FlashpackSubject } from "../../common/api";
import { GenericListImageBlock } from "../../common/CommonStyles";
import { ApiContext } from "../../common/context/ApiContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { LessonImage } from "../../common/LessonImage";
import { CELL_HEIGHT } from "../../common/constants";
import {
  COLOR_GRAY_BORDER,
  COLOR_GRAY_INACTIVE,
  COLOR_WHITE,
} from "../../common/colors";
import {
  AnalyticsContext,
  FlashpackSubjectCellTapEvent,
  ViewFlashpackSubjectsEvent,
} from "../../common/context/AnalyticsContext";

const SubjectListItem = styled.div`
  cursor: pointer;
  border-bottom: 1px solid ${COLOR_GRAY_BORDER};
  overflow: hidden;
  height: ${CELL_HEIGHT}px;
  background-color: ${COLOR_WHITE};
`;

const Title = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 21px;
  color: ${({ $isDisabled }) => ($isDisabled ? COLOR_GRAY_INACTIVE : "inherit")};
`;

const ComingSoon = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-size: 12px;
  line-height: 21px;
  color: ${COLOR_GRAY_INACTIVE};
`;

export const SubjectSelector = ({
  onSelectedSubject,
}: {
  onSelectedSubject: (subject: FlashpackSubject) => void;
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { getFlashpackSubjects } = React.useContext(ApiContext);
  const [subjects, setSubjects] = React.useState<FlashpackSubject[]>([]);
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    logEvent(new ViewFlashpackSubjectsEvent());
  }, [logEvent]);

  React.useEffect(() => {
    startLoading();
    getFlashpackSubjects().then(
      ({ subjects }: { subjects: FlashpackSubject[] }) => {
        setSubjects(subjects);
        stopLoading();
      }
    );
  }, [startLoading, stopLoading, setSubjects, getFlashpackSubjects]);

  return (
    <Container>
      {subjects.map((subject) => (
        <Row key={subject.title}>
          <Col xs={12} md={{ span: 10, offset: 1 }} lg={{ span: 8, offset: 2 }}>
            <SubjectListItem
              onClick={() => {
                logEvent(
                  new FlashpackSubjectCellTapEvent(subject.flashpack_type)
                );
                if (subject.is_available) {
                  onSelectedSubject(subject);
                }
              }}
            >
              <Stack direction="horizontal" style={{ height: "100%" }}>
                <GenericListImageBlock className="p-3">
                  <LessonImage
                    thumbSize="small"
                    url={subject.cover_image}
                    alt=""
                    style={{
                      borderRadius: "16px",
                      border: `1px solid ${COLOR_GRAY_BORDER}`,
                      opacity: subject.is_available ? "1.0" : "0.5",
                    }}
                  />
                </GenericListImageBlock>
                <Title $isDisabled={!subject.is_available}>
                  {subject.title}
                </Title>
                {!subject.is_available && (
                  <ComingSoon className="ms-auto p-3">
                    {t("flashpacks_subject_coming_soon")}
                  </ComingSoon>
                )}
              </Stack>
            </SubjectListItem>
          </Col>
        </Row>
      ))}
    </Container>
  );
};
